import React, { useState } from 'react';
import {
  getLanguageTranslation,
  getTranslationLanguageDisplay,
} from './translationLanguages';
import { Translations } from '../../../common/getTranslatedValue';
import { Modal } from './Modal';

export function LangsTargetModal({
  title,
  sourceText,
  languages,
  translations,
  onClose,
  onConfirm,
}: {
  title: string;
  sourceText: string;
  languages: readonly string[];
  translations: Translations | string | null | undefined;
  onClose: () => void;
  onConfirm: (languages: string[]) => void | Promise<void>;
}) {
  const targets = languages.map((language) => getTranslationLanguageDisplay(language));

  const [selected, setSelected] = useState(() =>
    targets
      .filter((display) => !getLanguageTranslation(translations, display.code))
      .map((display) => display.code),
  );

  const allCodes = targets.map((display) => display.code);
  const allSelected = allCodes.length > 0 && allCodes.every((code) => selected.includes(code));

  const toggle = (code: string) => {
    setSelected((current) =>
      current.includes(code)
        ? current.filter((item) => item !== code)
        : [...current, code],
    );
  };

  const toggleSelectAll = () => {
    setSelected(allSelected ? [] : allCodes);
  };

  const handleConfirm = () => {
    if (selected.length === 0) return;
    void onConfirm(selected);
    onClose();
  };

  return (
    <Modal
      title={title}
      onClose={onClose}
      width="small"
      actions={[
        {
          label: 'Annuler',
          preset: 'white',
          onClick: onClose,
        },
        {
          label: 'Confirmer',
          preset: 'primary',
          onClick: handleConfirm,
          disabled: selected.length === 0,
        },
      ]}
    >
      <p className="text-sm text-gray-600 mb-4 whitespace-pre-wrap break-words line-clamp-3">
        {sourceText}
      </p>
      <div className="flex justify-end mb-2">
        <button
          type="button"
          onClick={toggleSelectAll}
          disabled={allCodes.length === 0}
          className="text-sm font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-400"
        >
          {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
        </button>
      </div>
      <div className="space-y-2">
        {targets.map((display) => {
          const existing = getLanguageTranslation(translations, display.code);
          return (
            <label key={display.code} className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={selected.includes(display.code)}
                onChange={() => toggle(display.code)}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-xl leading-none shrink-0">{display.flag}</span>
              <span className="min-w-0 flex-1">
                <span className="block font-medium text-gray-900">
                  {display.name}
                  <span className="ml-2 text-xs font-normal text-gray-500">{display.code}</span>
                </span>
                {existing && (
                  <span className="block text-sm text-gray-600 mt-0.5 whitespace-pre-wrap break-words line-clamp-2">
                    <span className="text-amber-700 font-medium">Déjà rempli : </span>
                    {existing}
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </div>
    </Modal>
  );
}
